import { useState } from "react";
import { api } from "../lib/api.js";
import { coloringApi } from "./api.js";
import { Modal } from "../components/Modal.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { AUDIENCE_OPTIONS, DIFFICULTY_OPTIONS, TRIM_OPTIONS, type Audience, type Difficulty } from "./types.js";

/**
 * Setup wizard for a new Coloring Book: basics first, then the concept in the
 * author's own words. Everything entered here is editable afterwards in the
 * workspace — the wizard only seeds the project.
 */
export function ColoringWizard({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState<Audience>("kids");
  const [difficulty, setDifficulty] = useState<Difficulty>("simple");
  const [pageCount, setPageCount] = useState(30);
  const [trim, setTrim] = useState("8.5x11");
  const [customW, setCustomW] = useState(8.5);
  const [customH, setCustomH] = useState(11);
  const [concept, setConcept] = useState("");
  const [brief, setBrief] = useState("");
  const [developing, setDeveloping] = useState(false);

  const develop = async () => {
    if (concept.trim().length < 3) {
      toast.error("Describe your idea first.");
      return;
    }
    setDeveloping(true);
    try {
      const res = await coloringApi.developConceptDraft({
        title: title.trim() || undefined,
        idea: concept.trim(),
        audience,
        difficulty,
        pageCount
      });
      setBrief(res.brief);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not develop the concept.");
    } finally {
      setDeveloping(false);
    }
  };

  const next = () => {
    if (!title.trim()) {
      toast.error("A title is required.");
      return;
    }
    setStep(2);
  };

  const create = async () => {
    setSaving(true);
    try {
      const preset = TRIM_OPTIONS.find((t) => t.id === trim);
      const trimWidth = trim === "custom" ? customW : preset?.width ?? 8.5;
      const trimHeight = trim === "custom" ? customH : preset?.height ?? 11;
      const { project } = await api.createProject({
        type: "coloring",
        title: title.trim(),
        subtitle: subtitle.trim() || undefined,
        description: description.trim() || undefined,
        coloring: {
          audience,
          difficulty,
          pageCount: Math.max(1, Math.min(200, Math.round(pageCount) || 30)),
          trim,
          trimWidth,
          trimHeight,
          concept: concept.trim() || undefined,
          brief: brief.trim() || undefined
        }
      });
      toast.success("Coloring book created.");
      onCreated(project.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create the coloring book.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New coloring book"
      subtitle={step === 1 ? "Step 1 of 2 — the basics" : "Step 2 of 2 — your idea"}
      onClose={onClose}
      width={600}
      footer={
        <>
          {step === 2 && (
            <button className="btn btn-ghost" onClick={() => setStep(1)} disabled={saving}>Back</button>
          )}
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          {step === 1 ? (
            <button className="btn btn-primary" onClick={next}>Next</button>
          ) : (
            <button className="btn btn-primary" onClick={create} disabled={saving}>
              {saving ? "Creating…" : "Create coloring book"}
            </button>
          )}
        </>
      }
    >
      {step === 1 ? (
        <>
          <div className="field">
            <label className="label">Title</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="Cozy Ghosts" />
          </div>
          <div className="field">
            <label className="label">Subtitle</label>
            <input className="input" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="A coloring book of everyday haunts" />
          </div>
          <div className="field">
            <label className="label">Description</label>
            <textarea className="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Intended audience</label>
            <div className="choice-row">
              {AUDIENCE_OPTIONS.map((a) => (
                <button key={a.id} type="button" className={`choice ${audience === a.id ? "on" : ""}`} onClick={() => setAudience(a.id)}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row gap-3">
            <div className="grow field">
              <label className="label">Difficulty</label>
              <select className="select" value={difficulty} onChange={(e) => setDifficulty(e.target.value as Difficulty)}>
                {DIFFICULTY_OPTIONS.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 140 }}>
              <label className="label">Coloring pages</label>
              <input className="input" type="number" min={1} max={200} value={pageCount} onChange={(e) => setPageCount(Number(e.target.value))} />
            </div>
          </div>
          <div className="row gap-3">
            <div className="grow field">
              <label className="label">Trim size</label>
              <select className="select" value={trim} onChange={(e) => setTrim(e.target.value)}>
                {TRIM_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            {trim === "custom" && (
              <>
                <div className="field" style={{ width: 100 }}>
                  <label className="label">Width (in)</label>
                  <input className="input" type="number" step={0.125} min={2} max={20} value={customW} onChange={(e) => setCustomW(Number(e.target.value))} />
                </div>
                <div className="field" style={{ width: 100 }}>
                  <label className="label">Height (in)</label>
                  <input className="input" type="number" step={0.125} min={2} max={20} value={customH} onChange={(e) => setCustomH(Number(e.target.value))} />
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label className="label">Describe the idea</label>
            <textarea
              className="textarea"
              rows={5}
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              autoFocus
              placeholder="Adorable ghosts living normal cozy lives. Gardening, cooking, reading, shopping, going to cafes, etc."
            />
          </div>
          <div className="row gap-2" style={{ marginBottom: 10 }}>
            <button className="btn btn-sm" onClick={() => void develop()} disabled={developing || saving}>
              <Icon name={developing ? "clock" : "sparkles"} size={14} className={developing ? "spin" : ""} /> Develop Concept
            </button>
            <span className="faint" style={{ fontSize: 12 }}>Optional - turns your idea into a creative brief you can edit.</span>
          </div>
          {brief && (
            <div className="field">
              <label className="label">Creative brief</label>
              <textarea className="textarea" rows={6} value={brief} onChange={(e) => setBrief(e.target.value)} />
            </div>
          )}
          <p className="faint" style={{ fontSize: 12, marginTop: 8 }}>
            <Icon name="info" size={12} /> You can build the Style Bible and plan the pages from the workspace next. Nothing is generated (and no image
            credits are spent) until you say so.
          </p>
        </>
      )}
    </Modal>
  );
}
